import type { Href } from "expo-router";
import { router, useLocalSearchParams } from "expo-router";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  Linking,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  useWindowDimensions,
  View,
  type TextStyle,
  type ViewStyle,
} from "react-native";

import {
  getJudgeAssignment,
  saveScoreDraft,
  submitScoreFinal,
  type JudgeAssignmentDetail,
} from "../../../../../../lib/scoring";
import { COLORS, FONTS } from "../../../../../../lib/brand";
import { PANE_THEMES } from "../../../../../../components/brand/paneThemes";

type SaveStatusKind =
  | { kind: "idle"; lastSavedAt: number | null }
  | { kind: "saving" }
  | { kind: "error"; message: string }
  | { kind: "submitted"; submittedAt: number };

const THEME = PANE_THEMES.Judge;
const FOREST_MID = THEME.surfaceBg;
const FOREST_DEEP = COLORS.FOREST_DEEP.hex;
const AMBER = THEME.accent;
const SAGE = THEME.success;
const LINEN = THEME.textOnSurface;

const W = "rgba(255,255,255,1)";
const WS = "rgba(255,255,255,0.78)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const BORDER = "rgba(255,255,255,0.07)";
const RED = THEME.destructive;
const REQUIRED_RED = THEME.destructive;

const PAGE_MAX_W = 1320;
const NOTES_LIMIT = 1000;
const PRIVATE_NOTES_LIMIT = 500;

function timeAgo(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 5) return "just now";
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatBytes(n: number | null): string {
  if (n == null) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// Parse a raw input string into a sub-score in [0, 10] with up to 1 decimal,
// or null if blank. Returns { value, raw } so we can keep the visible string
// in lockstep with the parsed number — autosave uses `value`, the input
// shows `raw`.
function parseSubScore(s: string): { value: number | null; valid: boolean; raw: string } {
  const trimmed = s.trim();
  if (!trimmed) return { value: null, valid: true, raw: "" };
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0 || n > 10) return { value: null, valid: false, raw: trimmed };
  // Round to 2 decimals (the column type is numeric(4,2)).
  const rounded = Math.round(n * 100) / 100;
  return { value: rounded, valid: true, raw: trimmed };
}

// Compute the live weighted score from current inputs + the comp's weights.
// Returns null until every active category has a numeric sub_score.
function computeWeightedScore(
  inputs: Record<string, string>,
  weights: { id: string; weight_pct: number }[]
): number | null {
  if (weights.length === 0) return null;
  let sum = 0;
  for (const w of weights) {
    const parsed = parseSubScore(inputs[w.id] ?? "");
    if (!parsed.valid || parsed.value == null) return null;
    sum += parsed.value * (w.weight_pct / 100);
  }
  return Math.round(sum * 100) / 100;
}

export default function JudgeScoreSubmissionScreen() {
  const { id, submissionId } = useLocalSearchParams<{ id: string; submissionId: string }>();
  const { width } = useWindowDimensions();
  const twoColumn = width >= 1100;

  const [assignment, setAssignment] = useState<JudgeAssignmentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  // Per-category raw input strings, keyed by category_id. Hydrated on load
  // from assignment.myScore.category_scores. We keep raw strings (not
  // numbers) so a user typing "8." doesn't get truncated to 8 mid-keystroke.
  const [categoryInputs, setCategoryInputs] = useState<Record<string, string>>({});
  const [categoryErr, setCategoryErr] = useState<Record<string, string | null>>({});

  const [passedGate, setPassedGate] = useState<boolean | null>(null);
  const [notes, setNotes] = useState("");
  const [privateNotes, setPrivateNotes] = useState("");
  const [gateInputErr, setGateInputErr] = useState<string | null>(null);
  const [confirmArmed, setConfirmArmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatusKind>({ kind: "idle", lastSavedAt: null });

  const competitionId = typeof id === "string" ? id : "";
  const submissionIdStr = typeof submissionId === "string" ? submissionId : "";

  const isFinalized = saveStatus.kind === "submitted";

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dirtyRef = useRef(false);
  const inFlightRef = useRef(false);
  const categoryInputsRef = useRef<Record<string, string>>({});
  const passedGateRef = useRef<boolean | null>(null);
  const notesRef = useRef("");
  const privateNotesRef = useRef("");
  const isFinalizedRef = useRef(false);
  const assignmentRef = useRef<JudgeAssignmentDetail | null>(null);
  const flushNowRef = useRef<() => void>(() => {});

  useEffect(() => {
    categoryInputsRef.current = categoryInputs;
    passedGateRef.current = passedGate;
    notesRef.current = notes;
    privateNotesRef.current = privateNotes;
    isFinalizedRef.current = isFinalized;
    assignmentRef.current = assignment;
  });

  const liveWeightedScore = useMemo(() => {
    if (!assignment) return null;
    return computeWeightedScore(
      categoryInputs,
      assignment.rubric_weights.map((w) => ({ id: w.id, weight_pct: w.weight_pct }))
    );
  }, [assignment, categoryInputs]);

  function buildCategoryPayload(
    a: JudgeAssignmentDetail,
    inputs: Record<string, string>
  ): { value: { category_id: string; sub_score: number | null }[]; allValid: boolean } {
    const out: { category_id: string; sub_score: number | null }[] = [];
    let allValid = true;
    for (const w of a.rubric_weights) {
      const parsed = parseSubScore(inputs[w.id] ?? "");
      if (!parsed.valid) allValid = false;
      out.push({ category_id: w.id, sub_score: parsed.value });
    }
    return { value: out, allValid };
  }

  const runSave = useCallback(async () => {
    const a = assignmentRef.current;
    if (!a || isFinalizedRef.current) return;
    if (inFlightRef.current) {
      dirtyRef.current = true;
      return;
    }

    const { value: catPayload, allValid } = buildCategoryPayload(a, categoryInputsRef.current);
    if (!allValid) {
      // Don't block the autosave on partial-typing edge cases — drop the
      // bad category and save the rest. The per-field error message keeps
      // the user oriented.
      setSaveStatus({ kind: "error", message: "One of the sub-scores is out of range (0–10)." });
      return;
    }

    inFlightRef.current = true;
    dirtyRef.current = false;
    setSaveStatus({ kind: "saving" });

    const { error, score, updated_at } = await saveScoreDraft({
      competitionId,
      submissionId: a.submission.id,
      passedGate: passedGateRef.current,
      rubricNotes: notesRef.current.trim() || null,
      privateNotes: privateNotesRef.current.trim() || null,
      categoryScores: catPayload,
    });
    inFlightRef.current = false;

    if (error) {
      dirtyRef.current = true;
      setSaveStatus({ kind: "error", message: error.message });
      return;
    }

    // Reflect the server-computed weighted score back into local state so
    // the indicator and the live display stay in lockstep with the DB.
    setAssignment((prev) => (prev ? { ...prev, myScore: { ...prev.myScore, score, updated_at } } : prev));

    if (dirtyRef.current) {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        void runSave();
      }, 200);
    } else {
      setSaveStatus({ kind: "idle", lastSavedAt: Date.now() });
    }
  }, [competitionId]);

  function scheduleSave() {
    if (isFinalizedRef.current) return;
    dirtyRef.current = true;
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      void runSave();
    }, 2500);
  }

  function saveImmediate() {
    if (isFinalizedRef.current) return;
    dirtyRef.current = true;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    void runSave();
  }

  const flushNow = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (dirtyRef.current && !isFinalizedRef.current) {
      void runSave();
    }
  }, [runSave]);

  useEffect(() => {
    flushNowRef.current = flushNow;
  }, [flushNow]);

  const load = useCallback(async () => {
    if (!competitionId || !submissionIdStr) return;
    setLoading(true);
    setLoadErr(null);
    const data = await getJudgeAssignment(competitionId, submissionIdStr);
    if (!data) {
      setLoadErr("This assignment is unavailable. It may have been re-assigned or revoked.");
      setLoading(false);
      return;
    }
    setAssignment(data);
    const inputs: Record<string, string> = {};
    for (const cs of data.myScore.category_scores) {
      inputs[cs.category_id] = cs.sub_score == null ? "" : String(cs.sub_score);
    }
    setCategoryInputs(inputs);
    setCategoryErr({});
    setPassedGate(data.myScore.passed_gate);
    setNotes(data.myScore.rubric_notes ?? "");
    setPrivateNotes(data.myScore.private_notes ?? "");
    const updatedMs = data.myScore.updated_at ? new Date(data.myScore.updated_at).getTime() : null;
    if (data.myScore.status === "submitted" && updatedMs != null) {
      setSaveStatus({ kind: "submitted", submittedAt: updatedMs });
    } else if (data.myScore.status === "draft" && updatedMs != null) {
      setSaveStatus({ kind: "idle", lastSavedAt: updatedMs });
    } else {
      setSaveStatus({ kind: "idle", lastSavedAt: null });
    }
    dirtyRef.current = false;
    setLoading(false);
  }, [competitionId, submissionIdStr]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (Platform.OS !== "web") return;
    const handler = () => {
      flushNowRef.current();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  useEffect(() => {
    return () => {
      flushNowRef.current();
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  function setCategoryInput(categoryId: string, raw: string) {
    // Strip everything that's not a digit or a single decimal. "8.5", "0.5"
    // all valid; rejects letters, multiple dots, etc.
    const cleaned = raw.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
    setCategoryInputs((prev) => ({ ...prev, [categoryId]: cleaned }));
    const parsed = parseSubScore(cleaned);
    setCategoryErr((prev) => ({
      ...prev,
      [categoryId]: parsed.valid ? null : "Use 0–10.",
    }));
    scheduleSave();
  }

  function missingForSubmit(): { needsCategories: string[]; needsGate: boolean } {
    if (!assignment) return { needsCategories: [], needsGate: false };
    const needsCategories: string[] = [];
    for (const w of assignment.rubric_weights) {
      const parsed = parseSubScore(categoryInputs[w.id] ?? "");
      if (parsed.value == null) needsCategories.push(w.name);
    }
    const needsGate = !!assignment.deliverable.is_locked_gate && passedGate == null;
    return { needsCategories, needsGate };
  }

  async function onSubmitFinal() {
    if (!assignment || submitting) return;
    const { needsCategories, needsGate } = missingForSubmit();

    if (!confirmArmed) {
      setGateInputErr(needsGate ? "Pick Pass or Fail to finalize." : null);
      const errs: Record<string, string | null> = { ...categoryErr };
      for (const w of assignment.rubric_weights) {
        const parsed = parseSubScore(categoryInputs[w.id] ?? "");
        if (parsed.value == null) errs[w.id] = "Required to finalize.";
      }
      setCategoryErr(errs);
      setConfirmArmed(true);
      setTimeout(() => setConfirmArmed(false), 4000);
      return;
    }

    if (needsCategories.length > 0 || needsGate) {
      setGateInputErr(needsGate ? "Pick Pass or Fail to finalize." : null);
      setConfirmArmed(false);
      return;
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    setSubmitting(true);
    setGateInputErr(null);
    const { value: catPayload } = buildCategoryPayload(assignment, categoryInputs);
    const { error, score, updated_at } = await submitScoreFinal({
      competitionId,
      submissionId: assignment.submission.id,
      passedGate,
      rubricNotes: notes.trim() || null,
      privateNotes: privateNotes.trim() || null,
      categoryScores: catPayload,
    });
    setSubmitting(false);
    if (error) {
      setSaveStatus({ kind: "error", message: error.message });
      setConfirmArmed(false);
      return;
    }
    const ts = updated_at ? new Date(updated_at).getTime() : Date.now();
    dirtyRef.current = false;
    setAssignment((prev) =>
      prev
        ? { ...prev, myScore: { ...prev.myScore, score, updated_at, status: "submitted" } }
        : prev
    );
    setSaveStatus({ kind: "submitted", submittedAt: ts });
    setConfirmArmed(false);
  }

  if (loading) {
    return (
      <View style={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
        <ActivityIndicator size="small" color={AMBER} />
      </View>
    );
  }

  if (loadErr || !assignment) {
    return (
      <View style={{ flex: 1, padding: 32 }}>
        <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 13, color: RED }}>
          {loadErr ?? "Submission not found."}
        </Text>
      </View>
    );
  }

  const fileType = (assignment.deliverable.file_type ?? "").toLowerCase();
  const isPdf = fileType === "pdf";
  const signedUrl = assignment.submission.signed_url;
  const weights = assignment.rubric_weights;
  const { needsCategories, needsGate } = missingForSubmit();
  const submitDisabled = isFinalized || submitting;
  const submitGated = needsCategories.length > 0 || needsGate;

  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 80 }}>
      <View style={{ paddingHorizontal: 32, paddingTop: 28, maxWidth: PAGE_MAX_W }}>
        <BackLink
          to={`/judge/competition/${competitionId}` as Href}
          label={`← Back to ${assignment.competition.name}`}
        />

        <View style={{ marginTop: 16, flexDirection: twoColumn ? "row" : "column", gap: 24 }}>
          {/* LEFT — submission viewer */}
          <View style={{ flex: twoColumn ? 1.5 : 1, minWidth: 0 }}>
            <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
              <View style={{ width: 22, height: 2, backgroundColor: AMBER, opacity: 0.85 }} />
              <Text
                style={{
                  fontFamily: FONTS.bodyBold,
                  fontSize: 10,
                  letterSpacing: 3,
                  textTransform: "uppercase",
                  color: AMBER,
                }}
              >
                Scoring submission
              </Text>
            </View>
            <Text
              style={{
                marginTop: 8,
                fontFamily: FONTS.displayMedium,
                fontSize: 22,
                color: LINEN,
              }}
            >
              {assignment.deliverable.name}
            </Text>
            <Text
              style={{
                marginTop: 4,
                fontFamily: FONTS.bodyLight,
                fontSize: 13,
                color: WM,
              }}
            >
              Team {assignment.team.name} · {assignment.submission.file_name}
              {formatBytes(assignment.submission.file_size_bytes)
                ? ` · ${formatBytes(assignment.submission.file_size_bytes)}`
                : ""}
              {assignment.submission.submitted_at
                ? ` · submitted ${timeAgo(assignment.submission.submitted_at)}`
                : ""}
            </Text>

            <View style={{ marginTop: 16 }}>
              {Platform.OS === "web" && isPdf && signedUrl ? (
                <View
                  style={{
                    borderRadius: 6,
                    overflow: "hidden",
                    borderWidth: 1,
                    borderColor: BORDER,
                    backgroundColor: "rgba(0,0,0,0.4)",
                    minHeight: 720,
                  }}
                >
                  {/* @ts-ignore — embed is a valid web element */}
                  <embed
                    src={signedUrl}
                    type="application/pdf"
                    style={{ width: "100%", height: 720, border: 0, display: "block" }}
                  />
                </View>
              ) : (
                <FileFallback
                  fileName={assignment.submission.file_name}
                  fileType={fileType}
                  signedUrl={signedUrl}
                />
              )}
            </View>

            {/* Team context */}
            <View
              style={{
                marginTop: 18,
                padding: 16,
                borderRadius: 6,
                backgroundColor: FOREST_MID,
                borderWidth: 1,
                borderColor: BORDER,
              }}
            >
              <Text
                style={{
                  fontFamily: FONTS.bodyBold,
                  fontSize: 9.5,
                  letterSpacing: 2,
                  textTransform: "uppercase",
                  color: AMBER,
                }}
              >
                Team
              </Text>
              <Text
                style={{
                  marginTop: 4,
                  fontFamily: FONTS.displayMedium,
                  fontSize: 17,
                  color: LINEN,
                }}
              >
                {assignment.team.name}
              </Text>

              <View style={{ marginTop: 12, flexDirection: "row", flexWrap: "wrap", gap: 10 }}>
                {assignment.team.members.length === 0 ? (
                  <Text style={{ fontFamily: FONTS.body, fontSize: 12, color: WL }}>
                    No team members listed.
                  </Text>
                ) : (
                  assignment.team.members.map((m) => (
                    <Pressable
                      key={m.student_id}
                      onPress={() => router.push(`/judge/student/${m.student_id}` as Href)}
                      style={[
                        {
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 8,
                          paddingHorizontal: 10,
                          paddingVertical: 6,
                          borderRadius: 999,
                          borderWidth: 1,
                          borderColor: BORDER,
                          backgroundColor: "rgba(255,255,255,0.04)",
                        } as ViewStyle,
                        Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                      ]}
                    >
                      <View
                        style={{
                          width: 22,
                          height: 22,
                          borderRadius: 999,
                          backgroundColor: "rgba(181,135,58,0.16)",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Text
                          style={{
                            fontFamily: FONTS.displaySemiBold,
                            fontSize: 11,
                            color: AMBER,
                          }}
                        >
                          {makeInitials(m.full_name)}
                        </Text>
                      </View>
                      <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12, color: W }}>
                        {m.full_name ?? "Member"}
                      </Text>
                    </Pressable>
                  ))
                )}
              </View>

              {assignment.team.other_submissions.length > 0 ? (
                <View style={{ marginTop: 16 }}>
                  <Text
                    style={{
                      fontFamily: FONTS.bodyBold,
                      fontSize: 9,
                      letterSpacing: 1.6,
                      textTransform: "uppercase",
                      color: WM,
                    }}
                  >
                    Other submissions from this team
                  </Text>
                  <View style={{ marginTop: 8, gap: 6 }}>
                    {assignment.team.other_submissions.map((s) => (
                      <Pressable
                        key={s.submission_id}
                        onPress={() =>
                          router.push(`/judge/competition/${competitionId}/score/${s.submission_id}` as Href)
                        }
                        style={Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null}
                      >
                        <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12, color: AMBER }}>
                          {s.deliverable_sequence}. {s.deliverable_name}
                          <Text style={{ color: WL }}> →</Text>
                        </Text>
                      </Pressable>
                    ))}
                  </View>
                </View>
              ) : null}
            </View>
          </View>

          {/* RIGHT — scoring form */}
          <View
            style={[
              { flex: twoColumn ? 1 : 1, minWidth: 0 } as ViewStyle,
              twoColumn && Platform.OS === "web"
                ? ({ position: "sticky", top: 16, alignSelf: "flex-start" } as unknown as ViewStyle)
                : null,
            ]}
          >
            {/* Sticky live Coravu score header */}
            <View
              style={{
                padding: 16,
                borderRadius: 6,
                backgroundColor: "rgba(181,135,58,0.10)",
                borderWidth: 1,
                borderColor: "rgba(181,135,58,0.40)",
              }}
            >
              <Text
                style={{
                  fontFamily: FONTS.bodyBold,
                  fontSize: 9.5,
                  letterSpacing: 2,
                  textTransform: "uppercase",
                  color: AMBER,
                }}
              >
                Current Coravu score
              </Text>
              <View style={{ marginTop: 6, flexDirection: "row", alignItems: "baseline", gap: 6 }}>
                <Text
                  style={{
                    fontFamily: FONTS.displayMedium,
                    fontSize: 36,
                    color: liveWeightedScore == null ? WL : LINEN,
                  }}
                >
                  {liveWeightedScore == null ? "—" : liveWeightedScore.toFixed(2)}
                </Text>
                <Text style={{ fontFamily: FONTS.body, fontSize: 13, color: WM }}>
                  / 10
                </Text>
              </View>
              <Text style={{ marginTop: 4, fontFamily: FONTS.body, fontSize: 11, color: WM }}>
                {liveWeightedScore == null
                  ? "Score every category to see the live weighted total."
                  : "Weighted using this competition's rubric."}
              </Text>
              {/* Eyebrow weights */}
              {weights.length > 0 ? (
                <Text style={{ marginTop: 8, fontFamily: FONTS.body, fontSize: 10.5, color: WL }}>
                  {weights.map((w) => `${w.name} ${w.weight_pct}%`).join(" · ")}
                </Text>
              ) : null}
            </View>

            {/* Save indicator */}
            <View style={{ marginTop: 10, alignItems: "flex-end" }}>
              <SaveIndicator status={saveStatus} onRetry={() => void runSave()} />
            </View>

            {/* Per-category cards */}
            <View style={{ marginTop: 6, gap: 10 }}>
              {weights.map((w) => (
                <CategoryCard
                  key={w.id}
                  name={w.name}
                  description={w.description}
                  weightPct={w.weight_pct}
                  value={categoryInputs[w.id] ?? ""}
                  err={categoryErr[w.id] ?? null}
                  disabled={isFinalized}
                  onChange={(v) => setCategoryInput(w.id, v)}
                  onBlur={() => flushNow()}
                />
              ))}
            </View>

            {/* Pass/Fail (gate only) */}
            {assignment.deliverable.is_locked_gate ? (
              <View
                style={{
                  marginTop: 14,
                  padding: 16,
                  borderRadius: 6,
                  backgroundColor: FOREST_MID,
                  borderWidth: 1,
                  borderColor: BORDER,
                }}
              >
                <FieldLabel required>Pass / Fail (gate)</FieldLabel>
                <View style={{ marginTop: 6, flexDirection: "row", gap: 8 }}>
                  <GateRadio
                    label="Pass"
                    tone="sage"
                    selected={passedGate === true}
                    disabled={isFinalized}
                    onPress={() => {
                      setPassedGate(true);
                      passedGateRef.current = true;
                      setGateInputErr(null);
                      saveImmediate();
                    }}
                  />
                  <GateRadio
                    label="Fail"
                    tone="red"
                    selected={passedGate === false}
                    disabled={isFinalized}
                    onPress={() => {
                      setPassedGate(false);
                      passedGateRef.current = false;
                      setGateInputErr(null);
                      saveImmediate();
                    }}
                  />
                </View>
                {gateInputErr ? (
                  <Text style={{ marginTop: 4, fontFamily: FONTS.bodyMedium, fontSize: 11, color: RED }}>
                    {gateInputErr}
                  </Text>
                ) : null}
              </View>
            ) : null}

            {/* Notes for the client */}
            <View
              style={{
                marginTop: 14,
                padding: 16,
                borderRadius: 6,
                backgroundColor: FOREST_MID,
                borderWidth: 1,
                borderColor: BORDER,
              }}
            >
              <FieldLabel>Notes for the client (optional)</FieldLabel>
              <TextInput
                value={notes}
                onChangeText={(v) => {
                  setNotes(v.slice(0, NOTES_LIMIT));
                  scheduleSave();
                }}
                onEndEditing={() => flushNow()}
                multiline
                textAlignVertical="top"
                editable={!isFinalized}
                placeholder="What stood out — pros, cons, the headline takeaway."
                placeholderTextColor={WL}
                style={{
                  marginTop: 6,
                  backgroundColor: "rgba(0,0,0,0.4)",
                  borderWidth: 1,
                  borderColor: BORDER,
                  borderRadius: 3,
                  paddingHorizontal: 12,
                  paddingVertical: 10,
                  minHeight: 80,
                  color: W,
                  fontFamily: FONTS.body,
                  fontSize: 12.5,
                  lineHeight: 19,
                  opacity: isFinalized ? 0.6 : 1,
                }}
              />
              <Text style={{ marginTop: 4, textAlign: "right", fontFamily: FONTS.body, fontSize: 10.5, color: WL }}>
                {notes.length} / {NOTES_LIMIT}
              </Text>

              <View style={{ marginTop: 10 }}>
                <FieldLabel>Private notes (only you see these)</FieldLabel>
                <TextInput
                  value={privateNotes}
                  onChangeText={(v) => {
                    setPrivateNotes(v.slice(0, PRIVATE_NOTES_LIMIT));
                    scheduleSave();
                  }}
                  onEndEditing={() => flushNow()}
                  multiline
                  textAlignVertical="top"
                  editable={!isFinalized}
                  placeholder="Reminders for yourself."
                  placeholderTextColor={WL}
                  style={{
                    marginTop: 6,
                    backgroundColor: "rgba(0,0,0,0.4)",
                    borderWidth: 1,
                    borderColor: BORDER,
                    borderRadius: 3,
                    paddingHorizontal: 12,
                    paddingVertical: 10,
                    minHeight: 60,
                    color: W,
                    fontFamily: FONTS.body,
                    fontSize: 12,
                    lineHeight: 18,
                    opacity: isFinalized ? 0.6 : 1,
                  }}
                />
                <Text style={{ marginTop: 4, textAlign: "right", fontFamily: FONTS.body, fontSize: 10.5, color: WL }}>
                  {privateNotes.length} / {PRIVATE_NOTES_LIMIT}
                </Text>
              </View>
            </View>

            {/* Submit button */}
            <View style={{ marginTop: 18, flexDirection: "row", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
              <Pressable
                onPress={() => void onSubmitFinal()}
                disabled={submitDisabled || (submitGated && !confirmArmed && false)}
                style={[
                  {
                    paddingHorizontal: 18,
                    paddingVertical: 10,
                    borderRadius: 3,
                    backgroundColor: submitGated ? "rgba(181,135,58,0.30)" : confirmArmed ? RED : AMBER,
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 8,
                    opacity: submitDisabled ? 0.5 : 1,
                  } as ViewStyle,
                  Platform.OS === "web" && !submitDisabled ? ({ cursor: "pointer" } as const) : null,
                ]}
              >
                {submitting ? <ActivityIndicator size="small" color={THEME.accentText} /> : null}
                <Text
                  style={{
                    fontFamily: FONTS.bodyBold,
                    fontSize: 11.5,
                    letterSpacing: 1.4,
                    textTransform: "uppercase",
                    color: confirmArmed ? COLORS.WHITE.hex : submitGated ? WS : THEME.accentText,
                  }}
                >
                  {isFinalized
                    ? "Submitted"
                    : submitting
                      ? "Submitting…"
                      : confirmArmed
                        ? "Click again to finalize"
                        : "Submit final score →"}
                </Text>
              </Pressable>
            </View>
            {submitGated && !isFinalized ? (
              <Text style={{ marginTop: 8, fontFamily: FONTS.body, fontSize: 11, color: WL }}>
                {needsCategories.length > 0
                  ? `Needs a score for: ${needsCategories.join(", ")}`
                  : null}
                {needsCategories.length > 0 && needsGate ? " · " : ""}
                {needsGate ? "Pass / Fail required" : ""}
              </Text>
            ) : null}
            {isFinalized ? (
              <Text style={{ marginTop: 8, fontFamily: FONTS.body, fontSize: 11, color: WL }}>
                Score is locked. Reach out if it needs to change.
              </Text>
            ) : null}

            {/* Assignment metadata */}
            <Text style={{ marginTop: 12, fontFamily: FONTS.body, fontSize: 10.5, color: WL }}>
              {assignment.deliverable.due_at
                ? `Due ${new Date(assignment.deliverable.due_at).toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                  })}`
                : "No due date"}
            </Text>
          </View>
        </View>
      </View>
    </ScrollView>
  );
}

function CategoryCard({
  name,
  description,
  weightPct,
  value,
  err,
  disabled,
  onChange,
  onBlur,
}: {
  name: string;
  description: string;
  weightPct: number;
  value: string;
  err: string | null;
  disabled: boolean;
  onChange: (v: string) => void;
  onBlur: () => void;
}) {
  return (
    <View
      style={{
        padding: 16,
        borderRadius: 6,
        backgroundColor: FOREST_MID,
        borderWidth: 1,
        borderColor: BORDER,
      }}
    >
      <View style={{ flexDirection: "row", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
        <Text
          style={{
            flex: 1,
            fontFamily: FONTS.displayMedium,
            fontSize: 19,
            color: LINEN,
          }}
        >
          {name}
        </Text>
        <View
          style={{
            paddingHorizontal: 8,
            paddingVertical: 3,
            borderRadius: 999,
            backgroundColor: "rgba(181,135,58,0.16)",
          }}
        >
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 10,
              letterSpacing: 1.2,
              color: AMBER,
            }}
          >
            {weightPct}%
          </Text>
        </View>
      </View>
      <Text style={{ marginTop: 6, fontFamily: FONTS.body, fontSize: 12, lineHeight: 18, color: WS }}>
        {description}
      </Text>
      <View style={{ marginTop: 12, flexDirection: "row", alignItems: "center", gap: 12 }}>
        <TextInput
          value={value}
          onChangeText={onChange}
          onEndEditing={onBlur}
          editable={!disabled}
          placeholder="0–10"
          placeholderTextColor={WL}
          keyboardType={Platform.OS === "web" ? "default" : "decimal-pad"}
          inputMode="decimal"
          style={
            {
              width: 100,
              backgroundColor: FOREST_DEEP,
              borderWidth: 1,
              borderColor: err ? "rgba(230,154,138,0.55)" : BORDER,
              borderRadius: 3,
              paddingHorizontal: 12,
              paddingVertical: 10,
              color: W,
              fontFamily: FONTS.displayMedium,
              fontSize: 22,
              opacity: disabled ? 0.6 : 1,
              ...(Platform.OS === "web" ? ({ outlineStyle: "none" } as object) : {}),
            } as unknown as TextStyle
          }
        />
        <Text style={{ flex: 1, fontFamily: FONTS.body, fontSize: 11, color: WL }}>
          0 = absent · 5 = competent · 10 = exceptional
        </Text>
      </View>
      {err ? (
        <Text style={{ marginTop: 4, fontFamily: FONTS.bodyMedium, fontSize: 11, color: RED }}>{err}</Text>
      ) : null}
    </View>
  );
}

function timeAgoMs(ms: number | null): string {
  if (ms == null) return "";
  const sec = Math.floor((Date.now() - ms) / 1000);
  if (sec < 5) return "just now";
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function makeInitials(s: string | null | undefined): string {
  if (!s?.trim()) return "??";
  const parts = s.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function FieldLabel({ children, required }: { children: string; required?: boolean }) {
  return (
    <View style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 9,
          letterSpacing: 1.6,
          textTransform: "uppercase",
          color: WM,
        }}
      >
        {children}
      </Text>
      {required ? <Text style={{ color: REQUIRED_RED, fontSize: 12, lineHeight: 12 }}>*</Text> : null}
    </View>
  );
}

const SaveIndicator = memo(function SaveIndicator({
  status,
  onRetry,
}: {
  status: SaveStatusKind;
  onRetry: () => void;
}) {
  const tickValue =
    status.kind === "idle"
      ? status.lastSavedAt
      : status.kind === "submitted"
        ? status.submittedAt
        : null;

  const [, force] = useState(0);
  useEffect(() => {
    if (tickValue == null) return;
    const id = setInterval(() => force((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [tickValue]);

  if (status.kind === "submitted") {
    return (
      <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
        <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: SAGE }} />
        <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11, color: SAGE }}>
          Submitted {timeAgoMs(status.submittedAt)}
        </Text>
      </View>
    );
  }
  if (status.kind === "saving") {
    return (
      <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
        <ActivityIndicator size="small" color={WL} />
        <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11, color: WL }}>Saving…</Text>
      </View>
    );
  }
  if (status.kind === "error") {
    return (
      <View style={{ flexDirection: "row", alignItems: "center", gap: 8, flexShrink: 1, maxWidth: 360 }}>
        <Text
          style={{ fontFamily: FONTS.bodyMedium, fontSize: 11, color: REQUIRED_RED, flexShrink: 1 }}
          numberOfLines={2}
        >
          {`Couldn't save — ${status.message}`}
        </Text>
        <Pressable
          onPress={onRetry}
          style={Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null}
        >
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 11,
              color: REQUIRED_RED,
              textDecorationLine: "underline",
            }}
          >
            Retry
          </Text>
        </Pressable>
      </View>
    );
  }
  if (status.lastSavedAt == null) return null;
  return (
    <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
      <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: SAGE }} />
      <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11, color: WS }}>
        Saved {timeAgoMs(status.lastSavedAt)}
      </Text>
    </View>
  );
});

function GateRadio({
  label,
  tone,
  selected,
  disabled,
  onPress,
}: {
  label: string;
  tone: "sage" | "red";
  selected: boolean;
  disabled?: boolean;
  onPress: () => void;
}) {
  const accent = tone === "sage" ? SAGE : RED;
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      style={[
        {
          flex: 1,
          paddingHorizontal: 12,
          paddingVertical: 10,
          borderRadius: 3,
          borderWidth: 1.5,
          borderColor: selected ? accent : BORDER,
          backgroundColor: selected
            ? `${tone === "sage" ? "rgba(143,187,138,0.15)" : "rgba(230,154,138,0.10)"}`
            : "transparent",
          opacity: disabled ? 0.5 : 1,
        } as ViewStyle,
        Platform.OS === "web" && !disabled ? ({ cursor: "pointer" } as const) : null,
      ]}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 11.5,
          letterSpacing: 1.4,
          textTransform: "uppercase",
          color: selected ? accent : WS,
          textAlign: "center",
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

function FileFallback({
  fileName,
  fileType,
  signedUrl,
}: {
  fileName: string;
  fileType: string;
  signedUrl: string | null;
}) {
  function open() {
    if (!signedUrl) return;
    if (Platform.OS === "web") {
      if (typeof window !== "undefined") window.open(signedUrl, "_blank", "noopener,noreferrer");
    } else {
      void Linking.openURL(signedUrl);
    }
  }
  return (
    <View
      style={{
        padding: 32,
        borderRadius: 6,
        borderWidth: 1,
        borderColor: BORDER,
        backgroundColor: FOREST_MID,
        alignItems: "center",
      }}
    >
      <Text
        style={{ fontFamily: FONTS.displayMedium, fontSize: 18, color: LINEN, textAlign: "center" }}
      >
        {fileName}
      </Text>
      <Text
        style={{
          marginTop: 6,
          fontFamily: FONTS.body,
          fontSize: 12,
          color: WM,
          textAlign: "center",
        }}
      >
        {fileType ? `${fileType.toUpperCase()} file. ` : ""}
        Open in a new tab to review.
      </Text>
      <Pressable
        onPress={open}
        disabled={!signedUrl}
        style={[
          {
            marginTop: 16,
            paddingHorizontal: 18,
            paddingVertical: 10,
            backgroundColor: AMBER,
            borderRadius: 3,
            opacity: signedUrl ? 1 : 0.4,
          } as ViewStyle,
          Platform.OS === "web" && signedUrl ? ({ cursor: "pointer" } as const) : null,
        ]}
      >
        <Text
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: 11.5,
            letterSpacing: 1.4,
            textTransform: "uppercase",
            color: THEME.accentText,
          }}
        >
          {"Open file →"}
        </Text>
      </Pressable>
    </View>
  );
}

function BackLink({ to, label }: { to: Href; label: string }) {
  return (
    <Pressable
      onPress={() => {
        if (router.canGoBack()) router.back();
        else router.replace(to);
      }}
      style={[
        Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
        { alignSelf: "flex-start", paddingVertical: 4 },
      ]}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyMedium,
          fontSize: 11.5,
          letterSpacing: 1.2,
          textTransform: "uppercase",
          color: WM,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}
